if (!customElements.get("variant-gallery")) {
  customElements.define(
    "variant-gallery",
    class VariantGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          mainGallery: this.querySelector(".main-gallery .variant-images"),
          thumbnailGallery: this.querySelector(
            ".thumbnail-gallery .thumbnail-slider"
          ),
        };

        if (!this.elements.mainGallery || !this.elements.thumbnailGallery)
          return;

        this.currentSlideIndex = 0;
        this.isNavigating = false; // Flag to prevent intersection observer conflicts
        this.initializeSlides();
        this.bindEvents();
        this.setupIntersectionObserver();
      }

      initializeSlides() {
        // Add data attributes to main gallery slides
        const mainSlides =
          this.elements.mainGallery.querySelectorAll(".image-slide");
        mainSlides.forEach((slide, index) => {
          slide.setAttribute("data-slide-index", index);
          slide.setAttribute("data-media-id", `media-${index}`);
          if (index === 0) {
            slide.classList.add("is-active");
          }
        });

        // Add data attributes to thumbnail slides
        const thumbnailSlides =
          this.elements.thumbnailGallery.querySelectorAll(".thumbnail-slide");
        thumbnailSlides.forEach((slide, index) => {
          slide.setAttribute("data-slide-index", index);
          slide.setAttribute("data-target", `media-${index}`);
          if (index === 0) {
            slide.classList.add("active");
          }
        });

        // Set current slide data attribute
        this.setAttribute("data-current-slide", this.currentSlideIndex);
      }

      bindEvents() {
        // Add click listeners to thumbnail slides
        this.elements.thumbnailGallery
          .querySelectorAll(".thumbnail-slide")
          .forEach((thumbnail) => {
            thumbnail.addEventListener("click", (e) => {
              e.preventDefault();
              const targetIndex = parseInt(
                thumbnail.getAttribute("data-slide-index")
              );
              this.goToSlide(targetIndex);
            });
          });
      }

      goToSlide(slideIndex) {
        const mainSlides =
          this.elements.mainGallery.querySelectorAll(".image-slide");
        const thumbnailSlides =
          this.elements.thumbnailGallery.querySelectorAll(".thumbnail-slide");

        if (slideIndex < 0 || slideIndex >= mainSlides.length) return;

        // Set navigation flag to prevent intersection observer conflicts
        this.isNavigating = true;

        // Update current slide index
        this.currentSlideIndex = slideIndex;
        this.setAttribute("data-current-slide", slideIndex);

        // Remove active classes from all slides
        mainSlides.forEach((slide) => slide.classList.remove("is-active"));
        thumbnailSlides.forEach((slide) => slide.classList.remove("active"));

        // Add active class to current slides
        const currentMainSlide = mainSlides[slideIndex];
        const currentThumbnailSlide = thumbnailSlides[slideIndex];

        currentMainSlide.classList.add("is-active");
        currentThumbnailSlide.classList.add("active");

        // Scroll main gallery to show the active slide
        this.scrollToSlide(currentMainSlide, this.elements.mainGallery);

        // Scroll thumbnail gallery to show the active thumbnail if needed
        this.scrollThumbnailIntoView(currentThumbnailSlide);

        // Reset navigation flag after scroll animation completes
        setTimeout(() => {
          this.isNavigating = false;
        }, 500); // Adjust timing based on scroll animation duration
      }

      scrollToSlide(slideElement, container) {
        const containerRect = container.getBoundingClientRect();
        const slideRect = slideElement.getBoundingClientRect();

        // Calculate the position to scroll to center the slide
        const scrollLeft =
          slideElement.offsetLeft - (containerRect.width - slideRect.width) / 2;

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });
      }

      scrollThumbnailIntoView(thumbnailElement) {
        const container = this.elements.thumbnailGallery;
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnailElement.getBoundingClientRect();

        // Check if thumbnail is already visible
        const isVisible =
          thumbnailRect.left >= containerRect.left &&
          thumbnailRect.right <= containerRect.right;

        if (!isVisible) {
          // Calculate scroll position to center the thumbnail
          const scrollLeft =
            thumbnailElement.offsetLeft -
            (containerRect.width - thumbnailRect.width) / 2;

          container.scrollTo({
            left: Math.max(0, scrollLeft),
            behavior: "smooth",
          });
        }
      }

      // Public method to go to specific slide (can be called from outside)
      setActiveSlide(slideIndex) {
        this.goToSlide(slideIndex);
      }

      // Public method to get current slide index
      getCurrentSlide() {
        return this.currentSlideIndex;
      }

      setupIntersectionObserver() {
        const options = {
          root: this.elements.mainGallery,
          rootMargin: '0px',
          threshold: 0.5 // Trigger when 50% of slide is visible
        };

        this.slideObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const slideIndex = parseInt(entry.target.getAttribute('data-slide-index'));
              this.updateActiveSlide(slideIndex);
            }
          });
        }, options);

        // Observe all main gallery slides
        const mainSlides = this.elements.mainGallery.querySelectorAll('.image-slide');
        mainSlides.forEach(slide => {
          this.slideObserver.observe(slide);
        });
      }

      updateActiveSlide(slideIndex) {
        // Don't update if we're currently navigating programmatically
        if (this.isNavigating) return;

        const mainSlides = this.elements.mainGallery.querySelectorAll('.image-slide');
        const thumbnailSlides = this.elements.thumbnailGallery.querySelectorAll('.thumbnail-slide');

        if (slideIndex < 0 || slideIndex >= mainSlides.length) return;

        // Only update if the slide index actually changed
        if (this.currentSlideIndex !== slideIndex) {
          this.currentSlideIndex = slideIndex;
          this.setAttribute('data-current-slide', slideIndex);

          // Remove active classes from all slides
          mainSlides.forEach(slide => slide.classList.remove('is-active'));
          thumbnailSlides.forEach(slide => slide.classList.remove('active'));

          // Add active class to current slides
          mainSlides[slideIndex].classList.add('is-active');
          thumbnailSlides[slideIndex].classList.add('active');

          // Scroll thumbnail gallery to show the active thumbnail if needed
          this.scrollThumbnailIntoView(thumbnailSlides[slideIndex]);
        }
      }
    }
  );
}
